import React, { useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { HiArrowDown, HiArrowRight } from 'react-icons/hi2';
import Lottie from 'lottie-react';
import successTick from '../assets/success-tick.json'; // Place this JSON in src/assets
import useExportPDF from '../hooks/useExportPDF';

export default function SuccessScreen() {
  const location = useLocation();
  const { exportPDF } = useExportPDF();

  const {
    deviceType = 'device',
    serialNumber = '',
    eui = '',
    signalStatus,
    metadata = [],
    deviceInfo
  } = location.state || {};

  // derive network info 
  const lw = deviceInfo?.network?.loraWan;
  const networkInfo = {
    spFact: lw?.spFact ?? null,           
    snr: lw?.snr ?? null,                 
    rssi: lw?.rssi ?? null,             
    lrrCnt: lw?.lrrCnt ?? null,            
    lastLrrId: lw?.lrrId ?? lw?.lrrList?.[0]?.lrrId ?? null
  };

  const pdfRef = useRef();

  useEffect (() => {
    exportPDF({ deviceType, serialNumber, eui, signalStatus, metadata, networkInfo })
  })

  const signalColor =
    signalStatus === 'Uplink OK'
      ? 'bg-green-100 text-green-700'
      : signalStatus === 'Weak Signal'
      ? 'bg-yellow-100 text-yellow-700'
      : 'bg-red-100 text-red-700';

  return (
    <div className="min-h-screen bg-gradient-to-br from-white to-blue-50 flex items-center justify-center px-4">
      <div className="bg-white border border-gray-200 shadow-lg rounded-xl max-w-md w-full p-8 text-center space-y-6">
        {/* Success Animation */}
        <div className="w-28 mx-auto">
          <Lottie animationData={successTick} loop={false} />
        </div>

        <h1 className="text-2xl font-semibold text-gray-800">Device Commissioned</h1>
        <p className="text-sm text-gray-600">Your <strong>{deviceType}</strong> has been successfully commissioned.</p>

        {/* Summary Block */}
        <div
          ref={pdfRef}
          className="bg-gray-50 border rounded-lg p-4 text-sm text-left space-y-2"
        >
          {serialNumber && <p><strong>Serial:</strong> {serialNumber}</p>}
          {eui && <p><strong>EUI:</strong> {eui}</p>}
          {signalStatus && (
            <p className="flex items-center gap-2">
              <strong>Signal:</strong>
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${signalColor}`}>
                {signalStatus}
              </span>
            </p>
          )}
          {metadata.length > 0 && (
            <div className="pt-2">
              <p className="font-medium mb-1">Metadata:</p>
              <div className="grid grid-cols-1 gap-1 text-sm text-gray-700">
                {metadata.map((item, i) => (
                  <div key={i} className="flex justify-between">
                    <span className="text-gray-500">{item.label}:</span>
                    <span className="font-medium text-gray-800">{item.value}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex flex-col gap-3 pt-4">
          <span class="text-sm">Summary has been automatically downloded. If not you can downlod it again.</span>
          <button
            onClick={() =>
              exportPDF({ deviceType, serialNumber, eui, signalStatus, metadata })
            }
            className="inline-flex items-center justify-center gap-2 px-5 py-2 rounded-lg border border-primary text-primary text-sm hover:bg-primary/10"
          >
            <HiArrowDown className="w-4 h-4" />
            Download Summary
          </button>
          <Link
            to="/"
            className="inline-flex items-center justify-center gap-2 px-5 py-2 rounded-lg bg-primary text-white text-sm hover:bg-primary/90 transition"
          >
            Commission Another
            <HiArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </div>
  );
}
